import useIsNLplan from '@common/src/hooks/useIsNLPlan';
import AdvisoryReportLanguageModal from '@risk-analysis/src/pages/recommendations/AdvisoryReportLanguageModal';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  HStack,
  useDisclosure,
} from '@wegroup/design-system';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Language } from '../../../../../../../../../types/Common';
import useTracking from '../../../../../../../../common/src/hooks/tracking/useTracking';
import {
  flowsDefaultMeta,
  FLOW_TYPE_META,
} from '../../../../../../../../common/src/hooks/useFlowMeta';
import CreateAdvisoryReportWarningDialog from '../../../../../../../../conversations/src/features/detail/shared/components/actions/CreateAdvisoryReportWarningDialog';
import { useConversations } from '../../../../../../../../conversations/src/features/overview/api/getConversations';
import { useCreateAdvisoryReportRevision } from '../../../../../../api/createAdvisoryReportRevision';
import ContinueReportModalBody from './ContinueReportModalBody';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  partyId: string;
}

const ContinueReportModal: React.FC<Props> = ({
  isOpen,
  onClose,
  partyId,
  reportId,
}) => {
  const { t } = useTranslation();
  const { isNL } = useIsNLplan();
  const dialog = useDisclosure();
  const { trackEvent } = useTracking();
  const {
    data: partyConversations,
    refetch,
    isLoading: isLoadingLastConversation,
  } = useConversations({
    partyId,
  });

  // Take last last companyscan or risk analysis conversation
  const conversation = partyConversations?.items.filter(({ flow_id }) =>
    ['risk-analysis', 'company-scan'].includes(
      (FLOW_TYPE_META[flow_id] || flowsDefaultMeta).path,
    ),
  )[0];
  const { mutateAsync, isLoading } = useCreateAdvisoryReportRevision();
  const history = useHistory();

  const [isEmptyReport, setIsEmptyReport] = useState(false);
  const {
    isOpen: isLanguagePickerOpen,
    onOpen: onOpenLanguageModal,
    onClose: closeLanguagePicker,
  } = useDisclosure();

  const handleFillInReport = () => {
    trackEvent('Continue report from Advisory Reports');
    history.push(
      `/advisory-report/new/personalize/${reportId}?backRef=/advisory-report/detail/${reportId}`,
    );
  };

  const handleCreateRevision = async (lang?: Language) => {
    if (isEmptyReport) {
      trackEvent('Create empty revision from Advisory Reports');
    } else {
      trackEvent('Create revision from Advisory Reports');
    }
    try {
      const newReport = await mutateAsync({
        reportId,
        isEmptyReport,
        conversationId: conversation?.id,
        recommendationId: conversation?.recommendation?.id,
        lang,
      });
      history.push(
        `/advisory-report/new/personalize/${newReport.id}?backRef=/advisory-report/detail/${newReport.id}`,
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (language?: Language) => {
    const { data } = await refetch();
    // Take last last companyscan or risk analysis conversation
    const updatedConversation = data?.items.filter(({ flow_id }) =>
      ['risk-analysis', 'company-scan'].includes(
        (FLOW_TYPE_META[flow_id] || flowsDefaultMeta).path,
      ),
    )[0];
    if (updatedConversation?.status === 'COMPLETED' || !updatedConversation) {
      handleCreateRevision(language);
    } else dialog.onOpen();
  };

  return (
    <>
      {conversation && (
        <CreateAdvisoryReportWarningDialog
          isOpen={dialog.isOpen}
          onClose={dialog.onClose}
          conversationId={conversation.id}
          flowId={conversation.flow_id}
          onSubmit={() =>
            isNL ? handleCreateRevision() : onOpenLanguageModal()
          }
        />
      )}
      {isLanguagePickerOpen && (
        <AdvisoryReportLanguageModal
          isOpen={isOpen}
          onClose={closeLanguagePicker}
          onLanguageSelected={handleSubmit}
        />
      )}
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(
              'ADVISORY-REPORTS_CREATION-FLOW_PERSONALISE_FILL-IN-ADVISORY-REPORT',
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ContinueReportModalBody
              isEmptyReport={isEmptyReport}
              setIsEmptyReport={setIsEmptyReport}
            />
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="secondary"
                onClick={() =>
                  isNL ? handleCreateRevision() : onOpenLanguageModal()
                }
                isLoading={isLoading || isLoadingLastConversation}
              >
                {t('ADVISORY-REPORTS_DETAIL_REVISION-MODAL_CREATE-REVISION')}
              </Button>
              <Button
                isDisabled={isLoading || isEmptyReport}
                onClick={handleFillInReport}
              >
                {t('ADVISORY-REPORTS_DETAIL_REVISION-MODAL_FILL-IN-REPORT')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ContinueReportModal;
